import { v4 as uuidv4 } from "uuid";
import moment, { Moment } from "moment";

export enum TaskStatus {
  TODO = "TODO",
  IN_PROGRESS = "IN_PROGRESS",
  DONE = "DONE",
}

export enum TaskPriority {
  LOW = "LOW",
  MEDIUM = "MEDIUM",
  HIGH = "HIGH",
}

export interface TaskInterface {
  id: string;
  title: string;
  description: string;
  status: TaskStatus;
  dueDate: Moment | null;
  priority: TaskPriority;
  createdAt: Moment;
  updatedAt: Moment;
  deletedAt: Moment | null;
  tagsIds: string[];
  categoryId: string | null;
}

class Task implements TaskInterface {
  readonly id;
  readonly title;
  readonly description;
  readonly status;
  readonly dueDate;
  readonly priority;
  readonly createdAt;
  readonly updatedAt;
  readonly deletedAt;
  readonly tagsIds;
  readonly categoryId;

  constructor(task?: Partial<TaskInterface>) {
    this.id = task?.id || uuidv4();
    this.title = task?.title || "";
    this.description = task?.description || "";
    this.status = task?.status || TaskStatus.TODO;
    this.dueDate = task?.dueDate || null;
    this.priority = task?.priority || TaskPriority.LOW;
    this.createdAt = task?.createdAt || moment();
    this.updatedAt = task?.updatedAt || moment();
    this.deletedAt = task?.deletedAt || null;
    this.tagsIds = task?.tagsIds || [];
    this.categoryId = task?.categoryId || null;
  }

  toJSON(): any {
    return {
      id: this.id,
      title: this.title,
      description: this.description,
      status: this.status,
      dueDate: this.dueDate ? this.dueDate.toISOString() : null,
      priority: this.priority,
      createdAt: this.createdAt.toISOString(),
      updatedAt: this.updatedAt.toISOString(),
      deletedAt: this.deletedAt ? this.deletedAt.toISOString() : null,
      tagsIds: this.tagsIds,
      categoryId: this.categoryId,
    };
  }

  static fromJSON(data: any): Task {
    return new Task({
      id: data.id,
      title: data.title,
      description: data.description,
      status: data.status,
      dueDate: data.dueDate ? moment(data.dueDate) : null,
      priority: data.priority,
      createdAt: moment(data.createdAt),
      updatedAt: moment(data.updatedAt),
      deletedAt: data.deletedAt ? moment(data.deletedAt) : null,
      tagsIds: data.tagsIds,
      categoryId: data.categoryId,
    });
  }

  static compareByDueDate(a: Task, b: Task): number {
    if (!a.dueDate && !b.dueDate) return 0;
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    return a.dueDate.valueOf() - b.dueDate.valueOf();
  }

  getTitle(): string {
    return this.title;
  }

  setTitle(title: string): Task {
    return new Task({ ...this, title, updatedAt: moment() });
  }

  getDescription(): string {
    return this.description;
  }

  setDescription(description: string): Task {
    return new Task({ ...this, description, updatedAt: moment() });
  }

  getStatus(): TaskStatus {
    return this.status;
  }

  setStatus(status: TaskStatus): Task {
    return new Task({ ...this, status, updatedAt: moment() });
  }

  getDueDate(): Moment | null {
    return this.dueDate;
  }

  setDueDate(dueDate: Moment | null): Task {
    return new Task({ ...this, dueDate, updatedAt: moment() });
  }

  getPriority(): TaskPriority {
    return this.priority;
  }

  setPriority(priority: TaskPriority): Task {
    return new Task({ ...this, priority, updatedAt: moment() });
  }

  getDeletedAt(): Moment | null {
    return this.deletedAt;
  }

  isDeleted(): boolean {
    return this.deletedAt !== null;
  }

  setDeletedAt(deletedAt: Moment | null): Task {
    return new Task({ ...this, deletedAt, updatedAt: moment() });
  }

  getTagsIds(): string[] {
    return this.tagsIds;
  }

  hasTagId(tagId: string): boolean {
    return this.tagsIds.includes(tagId);
  }

  setTagsIds(tagsIds: string[]): Task {
    return new Task({ ...this, tagsIds, updatedAt: moment() });
  }

  addTagId(tagId: string): Task {
    return new Task({
      ...this,
      tagsIds: [...this.tagsIds, tagId],
      updatedAt: moment(),
    });
  }

  removeTagId(tagId: string): Task {
    return new Task({
      ...this,
      tagsIds: this.tagsIds.filter((id) => id !== tagId),
      updatedAt: moment(),
    });
  }

  getCategoryId(): string | null {
    return this.categoryId;
  }

  isInCategory(categoryId: string): boolean {
    return this.categoryId === categoryId;
  }

  setCategoryId(categoryId: string | null): Task {
    return new Task({ ...this, categoryId, updatedAt: moment() });
  }
}

export default Task;
